import type { ApiClient } from '../apiClient';

/**
 * What the client is allowed to tell the assistant about the person asking.
 * Counts and a screen name — never a recipient, a title, or anything that was
 * recorded. The server whitelists these fields again on arrival; keeping the
 * type this narrow is what makes the server's whitelist a formality rather
 * than the only thing standing between a member's names and a model.
 */
export interface AssistantContext {
  screen?: string;
  hasVault?: boolean;
  recipientCount?: number;
  sealedMessageCount?: number;
}

export interface AssistantTurn {
  role: 'user' | 'assistant';
  text: string;
  /** Only ever on an assistant turn: an offer to open a screen. */
  action?: AssistantAction;
}

/**
 * An offer to move to a screen. The server picks it from a fixed list, so an
 * unfamiliar path here means the server changed, not that a model invented
 * somewhere to send us — but the panel still treats it as untrusted and only
 * navigates when the member presses the button.
 */
export interface AssistantAction {
  kind: 'navigate';
  key: string;
  path: string;
  label: string;
}

export function askAssistant(
  api: ApiClient,
  question: string,
  history: AssistantTurn[],
  context: AssistantContext,
  audience: 'member' | 'advisor' = 'member',
): Promise<{ answer: string; action?: AssistantAction; voiceAvailable?: boolean }> {
  return api.post<{ answer: string; action?: AssistantAction; voiceAvailable?: boolean }>('/api/assistant/ask', {
    question,
    audience,
    history,
    context,
  });
}
